import React from 'react';
import { Check, ArrowRight, Presentation, Clock, Users, Lightbulb, Mic, MessageCircle, BarChart2, Zap } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function Talks() {
  const {
    t,
    language
  } = useLanguage();
  const isIS = language === 'is';
  const talks = [{
    id: 'short',
    icon: <Lightbulb size={32} data-id="element-1670" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    borderColor: 'border-coral/20',
    tKey: 'short'
  }, {
    id: 'standard',
    icon: <Presentation size={32} data-id="element-1671" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    borderColor: 'border-teal/20',
    tKey: 'standard'
  }, {
    id: 'deep-dive',
    icon: <MessageCircle size={32} data-id="element-1672" />,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    tKey: 'deepDive'
  }];
  // Updated topic list - matched to actual writing and content areas
  const topics = [{
    icon: <Users size={24} data-id="element-1673" />,
    title: isIS ? 'Leiðtogi og stjórnandi' : 'Leadership & management',
    description: isIS ? 'Hvað rannsóknirnar segja um hvað gerir leiðtoga áhrifaríka - og hvað við höldum ranglega' : 'What the research says about what makes leaders effective - and what we wrongly assume does'
  }, {
    icon: <MessageCircle size={24} data-id="element-1674" />,
    title: isIS ? 'Endurgjöf og þróun' : 'Feedback & development',
    description: isIS ? 'Hvers vegna hefðbundið endurgjöfarlikan virkar gegn námi - og hvað virkar í staðinn' : 'Why the standard feedback model works against learning - and what actually helps people develop'
  }, {
    icon: <BarChart2 size={24} data-id="element-1675" />,
    title: isIS ? 'Stefna og val' : 'Strategy & choices',
    description: isIS ? 'Hvað stefna er í raun, hvers vegna þe flest fyrirtæki hafa ekki eina, og hvernig gott óðal líður' : "What strategy actually is, why most organisations don't have one, and what good strategic logic looks like"
  }, {
    icon: <Zap size={24} data-id="element-1676" />,
    title: isIS ? 'Hröðun og flóknar aðstæður' : 'Agility & complexity',
    description: isIS ? 'Hvað Agile-hreyfingin lærði um hvernig fyrirtæki geta flokkst í flóknum heimi' : 'What the Agile movement actually learned about how organisations can navigate a complex environment'
  }, {
    icon: <Users size={24} data-id="element-1677" />,
    title: isIS ? 'Hvernig teymi þróast' : 'How teams develop',
    description: isIS ? 'Tuckman, Wheelan og aðrar rannsóknir - hvernig hópar verða teymi, eða gera það ekki' : 'Tuckman, Wheelan and other research on how groups become teams - or consistently fail to'
  }, {
    icon: <MessageCircle size={24} data-id="element-1678" />,
    title: isIS ? 'Ágreiningar og krefjandi samtöl' : 'Conflict & difficult conversations',
    description: isIS ? 'Ágreiningur er ekki vandinn - forðunin er vandinn. Hvað rannsóknir sægja um hvernig á að taka á erfiðum samtölum' : "Conflict isn't the problem - avoidance is. What the research shows about how to approach difficult conversations"
  }];
  return <PageTransition data-id="element-1679">
      <div className="pt-12 pb-24" data-id="element-1680">
        <section className="text-center max-w-4xl mx-auto px-4 mb-20 relative" data-id="element-1681">
          <DecorativeShape type="square" color="bg-teal/10" className="top-0 right-0 w-32 h-32 rotate-12 -z-10" data-id="element-1682" />
          <DecorativeShape type="circle" color="bg-coral/10" className="bottom-0 left-0 w-40 h-40 -z-10" data-id="element-1683" />
          <Animate data-id="element-1684">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-1685">
              {t('talks.badge')}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-1686">
              {t('talks.title')} <br data-id="element-1687" />
              <span className="text-teal" data-id="element-1688">{t('talks.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1689">
              {t('talks.subtitle')}
            </p>
          </Animate>
        </section>

        {/* Formats */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-1690">
          <Animate className="text-center mb-12" data-id="element-1691">
            <h2 className="text-3xl font-bold text-charcoal mb-4" data-id="element-1692">
              {t('talks.chooseTitle')}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto" data-id="element-1693">
              {t('talks.chooseSubtitle')}
            </p>
          </Animate>
          <div className="space-y-8" data-id="element-1694">
            {talks.map((talk, index) => {
            const topics_list = t(`talks.items.${talk.tKey}.topics`) as string[];
            const format = t(`talks.items.${talk.tKey}.format`) as string[];
            return <Animate key={talk.id} delay={0} data-id="element-1695">
                  <Card className={`p-0 overflow-hidden border-2 ${talk.borderColor}`} data-id="element-1696">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-1697">
                      <div className={`lg:col-span-4 ${talk.lightColor} p-10 flex flex-col justify-center items-center text-center`} data-id="element-1698">
                        <div className={`w-20 h-20 rounded-full ${talk.color} text-white flex items-center justify-center mb-6 shadow-lg`} data-id="element-1699">
                          {talk.icon}
                        </div>
                        <h3 className={`text-2xl font-bold ${talk.textColor} mb-2`} data-id="element-1700">
                          {t(`talks.items.${talk.tKey}.title`)}
                        </h3>
                        <div className="flex items-center gap-2 mb-2" data-id="element-1701">
                          <Clock size={18} className={talk.textColor} data-id="element-1702" />
                          <span className={`font-bold ${talk.textColor}`} data-id="element-1703">
                            {t(`talks.items.${talk.tKey}.duration`)}
                          </span>
                        </div>
                        <p className="font-medium text-gray-600" data-id="element-1704">
                          {t(`talks.items.${talk.tKey}.subtitle`)}
                        </p>
                      </div>
                      <div className="lg:col-span-8 p-8 md:p-12 flex flex-col justify-center bg-white" data-id="element-1705">
                        <p className="text-lg text-gray-600 mb-6 leading-relaxed" data-id="element-1706">
                          {t(`talks.items.${talk.tKey}.description`)}
                        </p>
                        <div className="mb-6 p-4 bg-gray-50 rounded-xl" data-id="element-1707">
                          <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-1708">
                            {t('talks.idealFor')}
                          </p>
                          <p className="text-gray-700" data-id="element-1709">
                            {t(`talks.items.${talk.tKey}.idealFor`)}
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-1710">
                          <div data-id="element-1711">
                            <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-1712">
                              {t('talks.topicsLabel')}
                            </p>
                            <div className="space-y-2" data-id="element-1713">
                              {topics_list.map((topic: string, i: number) => <div key={i} className="flex items-center" data-id="element-1714">
                                  <div className={`mr-3 p-1 rounded-full ${talk.lightColor} ${talk.textColor}`} data-id="element-1715">
                                    <Check size={12} strokeWidth={3} data-id="element-1716" />
                                  </div>
                                  <span className="text-gray-700" data-id="element-1717">{topic}</span>
                                </div>)}
                            </div>
                          </div>
                          <div data-id="element-1718">
                            <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-1719">
                              {t('talks.formatLabel')}
                            </p>
                            <div className="space-y-2" data-id="element-1720">
                              {format.map((item: string, i: number) => <div key={i} className="flex items-start" data-id="element-1721">
                                  <div className={`mt-1 mr-3 p-1 rounded-full ${talk.lightColor} ${talk.textColor}`} data-id="element-1722">
                                    <Check size={12} strokeWidth={3} data-id="element-1723" />
                                  </div>
                                  <span className="text-gray-700 text-sm" data-id="element-1724">
                                    {item}
                                  </span>
                                </div>)}
                            </div>
                          </div>
                        </div>
                        <div className="pt-6 border-t border-gray-100" data-id="element-1725">
                          <Link to="/contact" data-id="element-1726">
                            <Button variant={index === 1 ? 'secondary' : 'primary'} rightIcon={<ArrowRight size={18} data-id="element-1728" />} data-id="element-1727">
                              {t('talks.bookTalk')}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Animate>;
          })}
          </div>
        </section>

        {/* Topics - updated to match actual content */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-1729">
          <Animate className="text-center mb-12" data-id="element-1730">
            <h2 className="text-3xl font-bold text-charcoal mb-4" data-id="element-1731">
              {t('talks.popularTopics')}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto" data-id="element-1732">
              {t('talks.popularTopicsSubtitle')}
            </p>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1733">
            {topics.map((topic, index) => <Animate key={index} delay={index * 0.08} data-id="element-1734">
                <Card className="p-6 text-center h-full border-2 border-gray-100" data-id="element-1735">
                  <div className="w-14 h-14 mx-auto mb-4 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-600" data-id="element-1736">
                    {topic.icon}
                  </div>
                  <h3 className="font-bold text-charcoal mb-2" data-id="element-1737">
                    {topic.title}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-1738">{topic.description}</p>
                </Card>
              </Animate>)}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-1739">
          <Animate data-id="element-1740">
            <Card className="p-12 bg-gradient-to-br from-teal/5 to-coral/5 border-0" data-id="element-1741">
              <div className="w-16 h-16 mx-auto mb-6 bg-teal/10 rounded-full flex items-center justify-center" data-id="element-1742">
                <Mic size={32} className="text-teal" data-id="element-1743" />
              </div>
              <h2 className="text-3xl font-bold mb-4" data-id="element-1744">
                {t('talks.customTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-1745">
                {t('talks.customDescription')}
              </p>
              <Link to="/contact" data-id="element-1746">
                <Button variant="secondary" rightIcon={<ArrowRight size={18} data-id="element-1748" />} data-id="element-1747">
                  {t('talks.customCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}